import {
  FaBolt,
  FaClock,
  FaCodeBranch,
  FaCommentDots,
  FaCog,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaFilter,
  FaHourglassHalf,
  FaMagic,
  FaPaperPlane,
  FaPlay,
  FaSearch
} from 'react-icons/fa'
import type { IconType } from 'react-icons'
import type { DefaultTheme } from 'styled-components'
import { LucaIcon } from '@components'
import { ValidLocale } from '@localisation'
import { AuditTrailCommentConfigForm } from '../builder/AuditTrailCommentConfigForm'
import { CreateDocumentConfigForm } from '../builder/createDocument/CreateDocumentConfigForm'
import { EmailSendConfigForm } from '../builder/EmailSendConfigForm'
import { EventTriggerConfigForm } from '../builder/EventTriggerConfigForm'
import { FilterNodeConfigForm } from '../builder/FilterNodeConfigForm'
import { LucaConfigForm } from '../builder/LucaConfigForm'
import { SelectorNodeConfigForm } from '../builder/SelectorNodeConfigForm'
import { ScheduledTriggerConfigForm } from '../builder/ScheduledTriggerConfigForm'
import { WaitConfigForm } from '../builder/WaitConfigForm'
import { DEFAULT_FILTER_CONFIG } from './filterConfigUtils'
import {
  summarizeAuditTrailCommentConfig,
  summarizeCreateDocumentConfig,
  summarizeEmailSendConfig,
  summarizeEventTriggerConfig,
  summarizeFilterConfig,
  summarizeLucaConfig,
  summarizeOpaqueConfig,
  summarizeScheduledTriggerConfig,
  summarizeSelectorConfig,
  summarizeWaitConfig
} from './nodeSummarizers'
import {
  NodeCategory,
  NODE_CATEGORIES,
  NodeTypeDefinition,
  WORKFLOW_NODE_TYPES,
  WorkflowNodeType
} from './types'

const FILTER_QUERY_NODE_SHARED = {
  accentColor: (theme: DefaultTheme) => theme.colors.primary,
  defaultConfig: DEFAULT_FILTER_CONFIG,
  category: 'logic' as const
}

export const NODE_REGISTRY: { [K in WorkflowNodeType]: NodeTypeDefinition<K> } = {
  scheduled_trigger: {
    type: 'scheduled_trigger',
    labelKey: 'workflows.nodes.scheduledTrigger.label',
    descriptionKey: 'workflows.nodes.scheduledTrigger.description',
    icon: FaClock,
    accentColor: theme => theme.colors.warning,
    handles: [{ id: 'out', type: 'source' }],
    defaultConfig: {
      frequency: 'daily',
      hour: 8,
      minute: 0
    },
    paletteOrder: 1,
    category: 'trigger',
    singleton: true,
    summarize: (config, t) => summarizeScheduledTriggerConfig(config, t),
    configForm: ScheduledTriggerConfigForm
  },
  event_trigger: {
    type: 'event_trigger',
    labelKey: 'workflows.nodes.eventTrigger.label',
    descriptionKey: 'workflows.nodes.eventTrigger.description',
    icon: FaBolt,
    accentColor: theme => theme.colors.success,
    handles: [{ id: 'out', type: 'source' }],
    defaultConfig: { event_type: 'DOCUMENT_CREATED' },
    paletteOrder: 2,
    category: 'trigger',
    summarize: summarizeEventTriggerConfig,
    configForm: EventTriggerConfigForm
  },
  filter: {
    type: 'filter',
    labelKey: 'workflows.nodes.filter.label',
    descriptionKey: 'workflows.nodes.filter.description',
    icon: FaFilter,
    paletteOrder: 3,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    summarize: summarizeFilterConfig,
    ...FILTER_QUERY_NODE_SHARED,
    configForm: props => <FilterNodeConfigForm {...props} variant="filter" />
  },
  selector: {
    type: 'selector',
    labelKey: 'workflows.nodes.selector.label',
    descriptionKey: 'workflows.nodes.selector.description',
    icon: FaSearch,
    paletteOrder: 4,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    summarize: summarizeSelectorConfig,
    ...FILTER_QUERY_NODE_SHARED,
    configForm: SelectorNodeConfigForm
  },
  wait: {
    type: 'wait',
    labelKey: 'workflows.nodes.wait.label',
    descriptionKey: 'workflows.nodes.wait.description',
    icon: FaHourglassHalf,
    accentColor: theme => theme.colors.primary,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {
      amount: 1,
      unit: 'hours'
    },
    paletteOrder: 5,
    category: 'logic',
    summarize: (config, t) => summarizeWaitConfig(config, t),
    configForm: WaitConfigForm
  },
  luca: {
    type: 'luca',
    labelKey: 'workflows.nodes.luca.label',
    descriptionKey: 'workflows.nodes.luca.description',
    icon: LucaIcon,
    accentColor: theme => theme.luca.blue,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {
      instructions: '',
      approved_tools: []
    },
    paletteOrder: 6,
    category: 'ai',
    maxOutgoing: 1,
    clampSummary: false,
    providesTextOutput: true,
    summarize: (config, t, catalogs, options) => summarizeLucaConfig(config, t, catalogs, options),
    configForm: LucaConfigForm
  },
  audit_trail_comment: {
    type: 'audit_trail_comment',
    labelKey: 'workflows.nodes.auditTrailComment.label',
    descriptionKey: 'workflows.nodes.auditTrailComment.description',
    icon: FaCommentDots,
    accentColor: theme => theme.colors.dashboardExtra1,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {
      message: ''
    },
    paletteOrder: 8,
    category: 'action',
    summarize: config => summarizeAuditTrailCommentConfig(config),
    configForm: AuditTrailCommentConfigForm
  },
  email_send: {
    type: 'email_send',
    labelKey: 'workflows.nodes.emailSend.label',
    descriptionKey: 'workflows.nodes.emailSend.description',
    icon: FaEnvelope,
    accentColor: theme => theme.colors.dashboardExtra1,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {
      recipients: [],
      recipient_tags: [],
      subject: '',
      message_source: 'upstream',
      message: '',
      language: ValidLocale.FI
    },
    initialConfig: ({ language }) => ({ language }),
    paletteOrder: 9,
    category: 'action',
    summarize: (config, t, catalogs, options) =>
      summarizeEmailSendConfig(config, t, catalogs, options),
    configForm: EmailSendConfigForm
  },
  create_document: {
    type: 'create_document',
    labelKey: 'workflows.nodes.createDocument.label',
    descriptionKey: 'workflows.nodes.createDocument.description',
    icon: FaFileInvoiceDollar,
    accentColor: theme => theme.colors.dashboardExtra1,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {
      accounting_rule_id: null,
      date: { source: 'calc', rule: 'run' },
      as_draft: false,
      copy_attachments: true
    },
    cloneConfig: config => ({ ...structuredClone(config), accounting_rule_id: null }),
    paletteOrder: 7,
    category: 'action',
    inspectorWidth: 660,
    inspectorFlush: true,
    summarize: (config, t) => summarizeCreateDocumentConfig(config, t),
    configForm: CreateDocumentConfigForm
  },
  unknown: {
    type: 'unknown',
    labelKey: 'workflows.nodes.unknown.label',
    descriptionKey: 'workflows.nodes.unknown.description',
    icon: FaCog,
    accentColor: theme => theme.colors.metalGray,
    handles: [
      { id: 'in', type: 'target' },
      { id: 'out', type: 'source' }
    ],
    defaultConfig: {},
    paletteOrder: 99,
    category: 'action',
    paletteHidden: true,
    summarize: config => summarizeOpaqueConfig(config)
  }
}

const CATEGORY_LABEL_KEYS: Record<NodeCategory, string> = {
  trigger: 'workflows.palette.categories.trigger',
  logic: 'workflows.palette.categories.logic',
  ai: 'workflows.palette.categories.ai',
  action: 'workflows.palette.categories.action'
}

export const CATEGORY_PALETTE_META: Record<
  NodeCategory,
  {
    icon: IconType
    accentColor: (theme: DefaultTheme) => string
    labelKey: string
  }
> = {
  trigger: {
    icon: FaPlay,
    accentColor: theme => theme.colors.success,
    labelKey: CATEGORY_LABEL_KEYS.trigger
  },
  logic: {
    icon: FaCodeBranch,
    accentColor: theme => theme.colors.primary,
    labelKey: CATEGORY_LABEL_KEYS.logic
  },
  ai: {
    icon: FaMagic,
    accentColor: theme => theme.colors.AIPurple,
    labelKey: CATEGORY_LABEL_KEYS.ai
  },
  action: {
    icon: FaPaperPlane,
    accentColor: theme => theme.colors.dashboardExtra1,
    labelKey: CATEGORY_LABEL_KEYS.action
  }
}

export interface PaletteGroup {
  category: NodeCategory
  labelKey: string
  entries: NodeTypeDefinition[]
}

const resolveCategory = (category: NodeCategory | undefined): NodeCategory =>
  category && NODE_CATEGORIES.includes(category) ? category : 'action'

export const getNodeDefinition = (type?: string | null): NodeTypeDefinition =>
  NODE_REGISTRY[type as WorkflowNodeType] ?? NODE_REGISTRY.unknown

export const getPaletteEntries = (allowedTypes: string[]): PaletteGroup[] => {
  const grouped = new Map<NodeCategory, NodeTypeDefinition[]>()

  allowedTypes
    .map(type => NODE_REGISTRY[type as WorkflowNodeType])
    .filter(definition => Boolean(definition) && !definition.paletteHidden)
    .forEach(definition => {
      const category = resolveCategory(definition.category)
      const entries = grouped.get(category) ?? []
      entries.push(definition)
      grouped.set(category, entries)
    })

  return NODE_CATEGORIES.map(category => ({
    category,
    labelKey: CATEGORY_LABEL_KEYS[category],
    entries: (grouped.get(category) ?? []).sort((a, b) => a.paletteOrder - b.paletteOrder)
  })).filter(group => group.entries.length > 0)
}

export const REGISTERED_NODE_TYPES = WORKFLOW_NODE_TYPES
